import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../Api';

const EditFormation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');
  
  // LimeSurvey search states
  const [limeSurveys, setLimeSurveys] = useState([]);
  const [limeSearch, setLimeSearch] = useState('');
  const [showLimeDropdown, setShowLimeDropdown] = useState(false);
  const [selectedLimeSurvey, setSelectedLimeSurvey] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    survey_id: '',
    limesurvey_id: '',
    visibility: 'private',
    target_audience: 'all',
    is_active: true,
  });

  useEffect(() => {
    fetchFormation();
  }, [id]);

  // Debounced LimeSurvey search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (limeSearch.trim()) {
        searchLimeSurveys(limeSearch);
      } else {
        setLimeSurveys([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [limeSearch]);

  const fetchFormation = async () => {
    try {
      const response = await api.get(`/formations/${id}`);
      const data = response.data.data;
      setFormData({
        title: data.title || '',
        description: data.description || '',
        survey_id: data.survey_id || '',
        limesurvey_id: data.limesurvey_id || '',
        visibility: data.visibility || 'private',
        target_audience: data.target_audience || 'all',
        is_active: data.is_active ?? true,
      });
      
      // Set initial LimeSurvey search value
      if (data.limesurvey_id) {
        setLimeSearch(data.limesurvey_id);
      }
    } catch (error) {
      console.error('Error fetching formation:', error);
      setError('Erreur lors du chargement de la formation');
    } finally {
      setFetching(false);
    }
  };

  const searchLimeSurveys = async (searchTerm) => {
    try {
      const response = await api.get(`/formations/limesurvey/search?search=${encodeURIComponent(searchTerm)}`);
      
      if (response.data.success) {
        const surveysData = response.data.data || [];
        const surveysArray = Array.isArray(surveysData) ? surveysData : Object.values(surveysData);
        setLimeSurveys(surveysArray);
      } else {
        setLimeSurveys([]);
      }
    } catch (error) {
      console.error('Error searching LimeSurvey surveys:', error);
      console.error('Error details:', error.response?.data);
      setLimeSurveys([]);
    }
  };

  const handleLimeSurveySelect = (survey) => {
    setSelectedLimeSurvey(survey);
    setFormData(prev => ({
      ...prev,
      limesurvey_id: survey.limesurvey_id,
      survey_id: '', // Clear survey_id since we're using limesurvey_id
    }));
    setLimeSearch(survey.limesurvey_id);
    setShowLimeDropdown(false);
  };

  const handleLimeSearchChange = (e) => {
    const value = e.target.value;
    setLimeSearch(value);
    setShowLimeDropdown(true);
    
    // Clear selection if search is cleared
    if (!value.trim()) {
      setSelectedLimeSurvey(null);
      setFormData(prev => ({
        ...prev,
        limesurvey_id: '',
        survey_id: '',
      }));
    }
  };

  const handleLimeSearchFocus = () => {
    setShowLimeDropdown(true);
  };

  const handleLimeSearchBlur = () => {
    // Delay hiding to allow click on dropdown items
    setTimeout(() => {
      setShowLimeDropdown(false);
    }, 200);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // If limesurvey_id is selected but no survey_id, send survey_title for auto-creation
      const payload = {
        ...formData,
        survey_id: formData.survey_id || null,
        limesurvey_id: formData.limesurvey_id || null,
        survey_title: selectedLimeSurvey ? selectedLimeSurvey.title : null,
      };

      await api.put(`/formations/${id}`, payload);
      navigate(`/formations/${id}`);
    } catch (error) {
      console.error('Error updating formation:', error);
      setError(error.response?.data?.message || 'Erreur lors de la mise à jour de la formation');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>
      
      <div className="relative z-10 p-3 sm:p-4 md:p-6 animate-slide-up">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center mb-4 sm:mb-5 md:mb-6">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">
              MODIFIER LA FORMATION / BRIEF
            </h1>
          </div>

          {/* Form */}
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4 sm:p-5 md:p-6">
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titre <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                />
              </div>

              {/* LimeSurvey Search */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Survey LimeSurvey
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={limeSearch}
                    onChange={handleLimeSearchChange}
                    onFocus={handleLimeSearchFocus}
                    onBlur={handleLimeSearchBlur}
                    placeholder="Rechercher par ID ou titre..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                  />
                  {showLimeDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {limeSurveys.length > 0 ? (
                        limeSurveys.map(survey => (
                          <div
                            key={survey.limesurvey_id}
                            className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleLimeSurveySelect(survey);
                            }}
                          >
                            <div className="font-medium text-gray-900">
                              ID: {survey.limesurvey_id}
                            </div>
                            <div className="text-sm text-gray-500">
                              {survey.title}
                            </div>
                            <div className="text-xs text-gray-400">
                              Langue: {survey.language} | Statut: {survey.active ? 'Actif' : 'Inactif'}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-gray-500 text-center">
                          {limeSearch.trim() ? 'Aucune enquête trouvée' : 'Tapez pour rechercher...'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Recherchez l'enquête par ID ou titre dans LimeSurvey. Si l'enquête n'existe pas dans la base locale, elle sera créée automatiquement.
                </p>
                {selectedLimeSurvey && (
                  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="text-sm text-green-800">
                      <strong>Sélectionné:</strong> {selectedLimeSurvey.title}
                    </div>
                    <div className="text-xs text-green-600">
                      ID: {selectedLimeSurvey.limesurvey_id} | Langue: {selectedLimeSurvey.language}
                    </div>
                  </div>
                )}
              </div>

              {/* Visibility */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Visibilité
                </label>
                <select
                  name="visibility"
                  value={formData.visibility}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                >
                  <option value="private">Privé</option>
                  <option value="public">Public</option>
                </select>
              </div>

              {/* Target Audience */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Audience cible
                </label>
                <select
                  name="target_audience"
                  value={formData.target_audience}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                >
                  <option value="all">Tous</option>
                  <option value="interviewer">Enquêteurs</option>
                  <option value="controller">Contrôleurs</option>
                  <option value="supervisor">Prestataires</option>
                </select>
              </div>

              {/* Is Active */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                  Formation active
                </label>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => navigate(`/formations/${id}`)}
                  className="w-full sm:w-auto px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm sm:text-base"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm sm:text-base"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} className="mr-2" />
                      Enregistrer
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditFormation;

